import { Suspense, lazy, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import If from 'components/If';
import Spin from 'components/Spin';
import {
  selectIsUnlockInsuranceConfirmationVisible,
  selectIsUnlockInsuranceModalVisible,
  selectIsUnlockInsuranceSuccessVisible,
} from '../../services/unlockInsuranceSelectors';
import { resetUnlockInsuranceSurface } from '../../services/unlockInsuranceSlice';
import useUnlockInsuranceFlow from '../../hooks/useUnlockInsuranceFlow';

const UnlockInsuranceModal = lazy(() => import('../unlock-insurance-modal'));
const UnlockInsuranceConfirmationDialog = lazy(
  () => import('../unlock-insurance-confirmation-dialog'),
);

/**
 * The module's only renderer. Every entry point is behind an eligibility gate that the flow
 * itself flips, so a host-owned surface is unmounted before it can paint — this sits above
 * all of them and reads the surface from the slice instead.
 */
function UnlockInsuranceSurfaces() {
  const dispatch = useAppDispatch();
  const { handlePiiSuccess, closeSurface } = useUnlockInsuranceFlow();
  const isModalVisible = useAppSelector(selectIsUnlockInsuranceModalVisible);
  const isSuccessVisible = useAppSelector(
    selectIsUnlockInsuranceSuccessVisible,
  );
  const isConfirmationVisible = useAppSelector(
    selectIsUnlockInsuranceConfirmationVisible,
  );

  /* Surfaces are per-page-load: the persisted slice must not reopen a dialog after a reload. */
  useEffect(() => {
    dispatch(resetUnlockInsuranceSurface());
  }, [dispatch]);

  return (
    <Suspense fallback={<Spin fullscreen />}>
      <If condition={isModalVisible}>
        <UnlockInsuranceModal
          open
          showSuccess={isSuccessVisible}
          onPiiSuccess={handlePiiSuccess}
          onPiiError={closeSurface}
          onClose={closeSurface}
        />
      </If>
      <If condition={isConfirmationVisible}>
        <UnlockInsuranceConfirmationDialog open onClose={closeSurface} />
      </If>
    </Suspense>
  );
}

export default UnlockInsuranceSurfaces;
